import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { useAppInstallation } from "../utils/installApp";
import { usePageMeta } from "../utils/usePageMeta";
export default function InstallApp() {
  const { tr } = useAppTranslation();
  const [params] = useSearchParams();
  const preferred =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      ? "ios"
      : "android";
  const platform =
    preferred === "ios"
      ? "ios"
      : /Android/.test(navigator.userAgent)
      ? "android"
      : /Windows NT|Macintosh|X11|Linux/.test(navigator.userAgent)
      ? "desktop"
      : params.get("platform") === "desktop"
      ? "desktop"
      : params.get("platform") === "ios"
      ? "ios"
      : params.get("platform") === "android"
      ? "android"
      : preferred;
  const desktop = platform === "desktop";
  const { prompt, installed, install } = useAppInstallation();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [accepted, setAccepted] = useState(false);
  const directInstall = Boolean(prompt) || busy || accepted;
  usePageMeta(
    "Installer G1Sport sur mobile ou ordinateur",
    "Installez G1Sport sur Android, iPhone, iPad, PC ou Mac depuis votre navigateur."
  );
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 7 } }}>
      <Button component={Link} to="/">
         {tr("← Retour à G1Sport")} </Button>
      <Paper
        variant="outlined"
        sx={{ mt: 2, p: { xs: 3, sm: 4 }, borderRadius: 4 }}
      >
        <Box
          component="img"
          src="/g1s-icon-3d-512.png"
          alt="G1Sport"
          sx={{ width: 76, height: 76, borderRadius: 3, mb: 2 }}
        />
        <Typography variant="h4" component="h1" fontWeight={700}>
          {desktop
            ? tr("Installer G1Sport sur cet ordinateur")
            : tr("Installer G1Sport")}
        </Typography>
        <Typography color="text.secondary" sx={{ my: 2 }}>
          {desktop
            ? tr("Vous utilisez un ordinateur : l’installation se fera sur ce PC ou Mac, pas sur votre téléphone. Pour installer sur mobile, scannez le QR code avec votre téléphone.")
            : directInstall
            ? tr("Validez l’installation pour ajouter l’icône G1Sport à votre écran d’accueil.")
            : tr("Ajoutez l’icône G1Sport à votre écran d’accueil pour accéder directement à votre compte.")}
        </Typography>
        {installed ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
               {tr("G1Sport est ouvert en mode application.")} </Alert>
            <Button component={Link} to="/login" variant="contained" fullWidth>
               {tr("Accéder à mon compte")} </Button>
          </>
        ) : (
          <>
            {(prompt || busy) && !accepted && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<Download />}
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const result = await install();
                  setBusy(false);
                  setAccepted(result === "accepted");
                  setNotice(
                    result === "accepted"
                      ? desktop
                        ? "Demande acceptée. Confirmez dans le navigateur pour retrouver G1Sport parmi les applications de cet ordinateur."
                        : "Demande acceptée. Suivez la confirmation du navigateur, puis retrouvez G1Sport sur votre écran d’accueil."
                      : result === "dismissed"
                      ? "Installation annulée. Vous pouvez réessayer depuis le menu du navigateur."
                      : "Utilisez le menu du navigateur pour installer G1Sport."
                  );
                }}
              >
                {busy
                  ? tr("Ouverture…")
                  : desktop
                  ? tr("Installer sur cet ordinateur")
                  : tr("Installer G1Sport")}
              </Button>
            )}
            {notice && (
              <Alert severity="info" sx={{ mt: 2 }} role="status">
                {tr(notice)}
              </Alert>
            )}
            {!directInstall && (
              <>
                <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
                  {desktop
                    ? tr("Installer sur PC ou Mac")
                    : platform === "ios"
                    ? tr("Installer sur iPhone / iPad")
                    : prompt
                    ? tr("Ou depuis le menu de Chrome")
                    : tr("Installer avec Chrome")}
                </Typography>
                <Stack
                  component="ol"
                  spacing={2}
                  sx={{
                    pl: 2.5,
                    my: 2,
                    "& li": { paddingLeft: 0.5, lineHeight: 1.65 },
                  }}
                >
                  {(desktop
                    ? [
                        tr("Ouvrez G1Sport dans Chrome ou Edge sur cet ordinateur."),
                        tr("Cliquez sur l’icône d’installation dans la barre d’adresse, ou cherchez l’option d’installation dans le menu du navigateur."),
                        tr("Confirmez l’installation. G1Sport s’ouvrira dans sa propre fenêtre et sera accessible parmi vos applications."),
                      ]
                    : platform === "ios"
                    ? [
                        tr("Ouvrez cette page dans Safari, Chrome ou un navigateur iOS compatible sur votre iPhone ou iPad."),
                        tr("Ouvrez le menu Partager, puis choisissez « Sur l’écran d’accueil » ou « Ajouter à l’écran d’accueil »."),
                        tr("Activez « Ouvrir comme app web » si cette option est proposée, puis touchez « Ajouter »."),
                      ]
                    : [
                        tr("Ouvrez cette page dans Chrome sur votre téléphone Android."),
                        tr("Touchez le menu ⋮, puis « Ajouter à l’écran d’accueil » ou « Installer l’application »."),
                        tr("Choisissez « Installer » si cette option apparaît, puis confirmez."),
                      ]
                  ).map((step) => (
                    <Box component="li" key={tr(step)}>
                      {tr(step)}
                    </Box>
                  ))}
                </Stack>
                <Alert severity="info">
                  {platform === "ios"
                    ? tr("Sur iPhone, utilisez le menu Partager du navigateur et confirmez l’ajout. Si cette option est absente, essayez Safari ou mettez à jour votre navigateur et iOS.")
                    : tr("Si le bouton d’installation n’apparaît pas, utilisez le menu du navigateur. Les options varient selon sa version et peuvent être absentes si l’application est déjà installée.")}
                </Alert>
              </>
            )}
          </>
        )}
        {!directInstall && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
               {tr("Installation depuis le site, sans App Store ni Google Play. Les fonctionnalités nécessitent une connexion Internet. Vos données restent liées à votre compte G1Sport.")} </Typography>
            <Button component={Link} to="/login" sx={{ mt: 2 }}>
               {tr("Continuer dans le navigateur")} </Button>
          </>
        )}
      </Paper>
    </Container>
  );
}
